import React, { useState, useEffect } from 'react';
import { Routes as RouterRoutes, Route, Navigate, useNavigate } from 'react-router-dom';
import { getRoleFromToken, hasModuleAccess } from '../../utils/jwtUtils';
import './Admin.css';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './views/Dashboard';
import Users from './views/Users';
import UserDetails from './views/UserDetails';
import Gatemen from './views/Gatemen';
import Vehicles from './views/Vehicles';
import BulkService from './views/BulkService';
import ToastContainer from '../../Components/ToastContainer';
import Routes from './views/Routes';
import ConferenceRooms from './views/ConferenceRooms';
import ConferenceRoomDetails from './views/ConferenceRoomDetails';
import Amenities from './views/Amenities';
import RoomAvailability from './views/RoomAvailability';
import BookingCalendar from './views/BookingCalendar';
import BookRoom from './views/BookRoom';
import MyBookings from './views/MyBookings';
import BookingRequests from './views/BookingRequests';
import AllBookings from './views/AllBookings';
import GatemenApproval from './views/GatemenApproval';
import VehicleDashboard from './views/VehicleDashboard';
import AdminLocationManagement from './views/AdminLocationManagement';
import LocationMaster from './views/LocationMaster';
import ReportList from './views/ReportList';
import ReportBuilder from './views/ReportBuilder';
import ReportViewer from './views/ReportViewer';
import RoleList from './views/RoleList';
import RoleBuilder from './views/RoleBuilder';
import MyProfile from './views/MyProfile';
import JobRequisitions from './views/JobRequisitions';
import CandidatePipeline from './views/CandidatePipeline';
import JobDashboard from './views/JobDashboard';
import JobMasterData from './views/JobMasterData';
import RecruiterAssignmentConfig from './views/RecruiterAssignmentConfig';
import ErrorLogs from './views/ErrorLogs';
import AuditLogs from './views/AuditLogs';
import DepartmentManagement from './views/DepartmentManagement';

function Admin() {
  const [role, setRole] = useState('Admin');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        navigate('/signin');
        return;
      }

      try {
        const claims = getRoleFromToken(token);
        const userRole = claims?.role?.toUpperCase();
        const modules = claims?.modules || [];

        // Core roles (SUPER_ADMIN, ADMIN, USER) always have access
        const isCoreRole = userRole === 'SUPER_ADMIN' || userRole === 'ADMIN' || userRole === 'USER';

        // Check if user has any admin-level modules
        const adminModules = [
          'USER_MANAGEMENT', 'ROLE_MANAGEMENT', 'DEPARTMENT_MANAGEMENT', 'LOCATION_MASTER',
          'CONFERENCE_ROOM', 'VEHICLE_ENROLLMENT', 'VEHICLE_ASSIGNMENT',
          'ROUTE_MANAGEMENT', 'VEHICLE_ANALYTICS', 'DASHBOARD', 'REPORTS',
          'ANALYTICS', 'SYSTEM_SETTINGS', 'AUDIT_LOG', 'JOB_REQUISITION',
          'CANDIDATE_MANAGEMENT', 'JOB_MASTER_DATA', 'JOB_DASHBOARD', 'JOB_REPORTS'
        ];
        const hasAdminModule = modules.some(m => adminModules.includes(m));

        if (isCoreRole || hasAdminModule) {
          setIsAuthenticated(true);
          setRole(userRole);
        } else {
          setIsAuthenticated(false);
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('authToken');
          navigate('/signin');
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        setIsAuthenticated(false);
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        navigate('/signin');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="admin-layout">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect to signin
  }

  return (
    <div className="admin-layout">
      <Sidebar />
      <div className="admin-content">
        <Header />
        <main className="admin-main">
          <RouterRoutes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard role={role} />} />
          <Route path="my-profile" element={<MyProfile />} />
          <Route path="community/users" element={<Users role={role} />} />
          <Route path="community/users/:userId" element={<UserDetails />} />
          <Route path="community/gatemen" element={<Gatemen />} />
          <Route path="community/gatemen-approval" element={<GatemenApproval />} />
          <Route path="community/vehicles" element={<Vehicles role={role} />} />
          <Route path="community/routes" element={<Routes />} />
          <Route path="community/conference-rooms" element={<ConferenceRooms />} />
          <Route path="community/conference-rooms/:roomId" element={<ConferenceRoomDetails />} />
          <Route path="community/amenities" element={<Amenities />} />
          <Route path="community/room-availability" element={<RoomAvailability />} />
          <Route path="community/booking-calendar" element={<BookingCalendar />} />
          <Route path="community/book-room" element={<BookRoom />} />
          <Route path="community/my-bookings" element={<MyBookings />} />
          <Route path="community/booking-requests" element={<BookingRequests />} />
          <Route path="community/all-bookings" element={<AllBookings />} />
                    <Route path="vehicles/dashboard" element={<VehicleDashboard />} />
          <Route path="bulk-service" element={<BulkService role={role} />} />
          <Route path="admin-locations" element={<AdminLocationManagement />} />
          <Route path="location-master" element={<LocationMaster />} />
          <Route path="department-master" element={<DepartmentManagement />} />
          <Route path="reports" element={<ReportList />} />
          <Route path="reports/builder" element={<ReportBuilder />} />
          <Route path="reports/builder/:reportId" element={<ReportBuilder />} />
          <Route path="reports/view/:reportId" element={<ReportViewer />} />
          <Route path="roles" element={<RoleList />} />
          <Route path="roles/builder" element={<RoleBuilder />} />
          <Route path="roles/builder/:roleId" element={<RoleBuilder />} />
          <Route path="roles/:roleId/permissions" element={<RoleBuilder />} />
          {/* Job Management Routes */}
          <Route path="jobs/dashboard" element={<JobDashboard />} />
          <Route path="jobs/requisitions" element={<JobRequisitions />} />
          <Route path="jobs/candidates" element={<CandidatePipeline />} />
          <Route path="jobs/master-data" element={<JobMasterData />} />
          <Route path="jobs/assignment-config" element={<RecruiterAssignmentConfig />} />
          {/* System Administration Routes - SuperAdmin Only */}
          <Route path="system/error-logs" element={<ErrorLogs />} />
          <Route path="system/audit-logs" element={<AuditLogs />} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </RouterRoutes>
      </main>
      </div>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
}

export default Admin;


